// projectDrawers — the platform-project edit surfaces, all on DrawerForm +
// useFormShell (ADR-008/ADR-038): Identity · Brand · Licensing & add-ons ·
// Countries, plus Manage trial. Each PATCHes /platform/projects/:id with its own
// field subset (partial update; the backend merges). IdentityDrawer is the template
// for a register-only drawer; the others bind controlled editors via watch/setValue.
// Each save rides the project's row stamp (ADR-084): the shell seeds from the project
// as it is at each open, the save carries the stamp, and a stale save shows the line
// with Load the latest (the project re-read).

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DrawerForm } from '../../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../../components/patterns/forms/useFormShell.js';
import { BrandFields } from '../../../components/patterns/BrandFields.js';
import { Input } from '../../../components/ui/Input.js';
import { Label } from '../../../components/ui/Label.js';
import { Textarea } from '../../../components/ui/Textarea.js';
import { Picker } from '../../../components/patterns/Picker.js';
import { countryName } from '../../../lib/format.js';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useUpdatePlatformProject } from '../../../hooks/usePlatformProjectMutations.js';
import { useDisplayZone } from '../../../hooks/useDisplayZone.js';
import { fetchPlatformProject, platformProjectsKeys, useLicenseTiers } from '../../../hooks/usePlatformProjects.js';
import type { PlatformProjectDetail } from '@ems-app/schemas/projects/platform-responses';
import { CountriesField, TierSeatsEditor, AddonToggles, useTrialLengthSource } from './projectEditors.js';
import {
  brandFormSchema,
  countriesFormSchema,
  identityFormSchema,
  licensingFormSchema,
  seedTiers,
  toTierInputs,
  trialExpiryISO,
  trialFormSchema,
  type BrandFormValues,
  type CountriesFormValues,
  type IdentityFormValues,
  type LicensingFormValues,
  type TierDraft,
  type TrialFormValues,
} from './projectForm.js';

interface DrawerProps {
  project: PlatformProjectDetail;
  open: boolean;
  onClose: () => void;
}

// The row every drawer opens on — the project as read (the stamp rides it) — and Load the latest: a quiet read (a
// project gone meanwhile is said in the drawer), into the page's entry once it answers.
function useProjectRow(project: PlatformProjectDetail) {
  const qc = useQueryClient();
  return {
    row: project,
    reload: () => readLatest(qc, platformProjectsKeys.detail(project.id), () => fetchPlatformProject(project.id, true)),
  };
}

// ── Identity ────────────────────────────────────────────────────────────────
export function IdentityDrawer({ project, open, onClose }: DrawerProps) {
  const { t } = useTranslation('platform-projects');
  const toast = useToast();
  const update = useUpdatePlatformProject();
  const stamped = useProjectRow(project);
  const shell = useFormShell<IdentityFormValues, PlatformProjectDetail>({
    schema: identityFormSchema,
    ...stamped,
    values: (r) => ({
      display_name: r?.display_name ?? '',
      mother_company: r?.mother_company ?? '',
      service_provider: r?.service_provider ?? '',
      service_provider_address: r?.service_provider_address ?? '',
      service_provider_support_email: r?.service_provider_support_email ?? '',
      description: r?.description ?? '',
    }),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({
        id: project.id,
        body: {
          row_version: stamp,
          display_name: v.display_name,
          mother_company: v.mother_company,
          service_provider: v.service_provider,
          service_provider_address: v.service_provider_address,
          service_provider_support_email: v.service_provider_support_email,
          description: v.description,
        },
      });
    },
    onSuccess: () => {
      toast.success(t('identityDrawer.done'));
      onClose();
    },
  });
  const err = shell.form.formState.errors;
  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('identityDrawer.title')} description={t('identityDrawer.description')} submitLabel={t('identityDrawer.submit')} autoFocusField="display_name">
      <div>
        <Label htmlFor="id-code">{t('fields.code')}</Label>
        <Input id="id-code" value={project.name} readOnly className="font-mono" />
        <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('identityDrawer.codeHelp')}</p>
      </div>
      <div>
        <Label htmlFor="id-display" required>
          {t('fields.displayName')}
        </Label>
        <Input id="id-display" {...shell.form.register('display_name')} error={err.display_name?.message} />
      </div>
      <div>
        <Label htmlFor="id-mother">{t('fields.motherCompany')}</Label>
        <Input id="id-mother" {...shell.form.register('mother_company')} error={err.mother_company?.message} />
      </div>
      <div>
        <Label htmlFor="id-provider">{t('fields.serviceProvider')}</Label>
        <Input id="id-provider" {...shell.form.register('service_provider')} error={err.service_provider?.message} />
        <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{t('identityDrawer.serviceProviderHelp')}</p>
      </div>
      <div>
        <Label htmlFor="id-provider-address">{t('fields.serviceProviderAddress')}</Label>
        <Input id="id-provider-address" {...shell.form.register('service_provider_address')} error={err.service_provider_address?.message} />
      </div>
      <div>
        <Label htmlFor="id-provider-support">{t('fields.serviceProviderSupportEmail')}</Label>
        <Input id="id-provider-support" type="email" {...shell.form.register('service_provider_support_email')} error={err.service_provider_support_email?.message} />
      </div>
      <div>
        <Label htmlFor="id-desc">{t('fields.description')}</Label>
        <Textarea id="id-desc" rows={3} {...shell.form.register('description')} error={err.description?.message} />
      </div>
    </DrawerForm>
  );
}

// ── Brand ─────────────────────────────────────────────────────────────────────
export function BrandDrawer({ project, open, onClose }: DrawerProps) {
  const { t } = useTranslation('platform-projects');
  const toast = useToast();
  const update = useUpdatePlatformProject();
  const stamped = useProjectRow(project);
  const shell = useFormShell<BrandFormValues, PlatformProjectDetail>({
    schema: brandFormSchema,
    ...stamped,
    values: (r) => ({ color_primary: r?.color_primary, color_on_primary: r?.color_on_primary }),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({ id: project.id, body: { row_version: stamp, color_primary: v.color_primary, color_on_primary: v.color_on_primary } });
    },
    onSuccess: () => {
      toast.success(t('brandDrawer.done'));
      onClose();
    },
  });
  const value = { color_primary: shell.form.watch('color_primary'), color_on_primary: shell.form.watch('color_on_primary') };
  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('brandDrawer.title')} description={t('brandDrawer.description')} submitLabel={t('brandDrawer.submit')}>
      <BrandFields
        value={value}
        onChange={(next) => {
          shell.form.setValue('color_primary', next.color_primary, { shouldDirty: true });
          shell.form.setValue('color_on_primary', next.color_on_primary, { shouldDirty: true });
        }}
      />
    </DrawerForm>
  );
}

// ── Countries ───────────────────────────────────────────────────────────────
export function CountriesDrawer({ project, open, onClose }: DrawerProps) {
  const { t, i18n } = useTranslation('platform-projects');
  const toast = useToast();
  const update = useUpdatePlatformProject();
  const stamped = useProjectRow(project);
  const shell = useFormShell<CountriesFormValues, PlatformProjectDetail>({
    schema: countriesFormSchema,
    ...stamped,
    values: (r) => ({ countries: r?.countries ?? [] }),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({ id: project.id, body: { row_version: stamp, countries: v.countries } });
    },
    onSuccess: () => {
      toast.success(t('countriesDrawer.done'));
      onClose();
    },
  });
  const countries = shell.form.watch('countries');
  const dropsDefault = project.default_country_iso_code !== null && countries.length > 0 && !countries.includes(project.default_country_iso_code);
  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('countriesDrawer.title')} description={t('countriesDrawer.description')} submitLabel={t('countriesDrawer.submit')}>
      <CountriesField
        value={countries}
        onChange={(c) => shell.form.setValue('countries', c, { shouldDirty: true, shouldValidate: !!shell.form.formState.errors.countries })}
        error={shell.form.formState.errors.countries ? t('wizard.countriesRequired') : undefined}
      />
      {/* Saving without the default country clears it (the server's rule) — said before, never found after. The live
          region is always there, so the sentence is heard when it appears. */}
      <p role="status" aria-live="polite" className={dropsDefault ? 'text-sm text-[var(--color-text-secondary)]' : 'sr-only'}>
        {dropsDefault ? t('countriesDrawer.defaultDropped', { country: countryName(project.default_country_iso_code!, i18n.language) }) : null}
      </p>
    </DrawerForm>
  );
}

// ── Licensing & add-ons ─────────────────────────────────────────────────────
export function AddonsLicensingDrawer({ project, open, onClose }: DrawerProps) {
  const { t } = useTranslation('platform-projects');
  const toast = useToast();
  const update = useUpdatePlatformProject();
  const catalogue = useLicenseTiers();
  const stamped = useProjectRow(project);
  const shell = useFormShell<LicensingFormValues, PlatformProjectDetail>({
    schema: licensingFormSchema,
    ...stamped,
    values: (r) => ({
      has_invoicing: r?.has_invoicing,
      has_field_force: r?.has_field_force,
      has_vehicles: r?.has_vehicles,
      tiers: seedTiers(catalogue.data ?? [], r?.tiers),
    }),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({
        id: project.id,
        body: {
          row_version: stamp,
          has_invoicing: v.has_invoicing,
          has_field_force: v.has_field_force,
          has_vehicles: v.has_vehicles,
          tiers: toTierInputs(v.tiers, project.type),
        },
      });
    },
    onSuccess: () => {
      toast.success(t('licensingDrawer.done'));
      onClose();
    },
  });
  // The catalogue may arrive after the open (the seed taken without it, no tiers); fill them once.
  useEffect(() => {
    if (catalogue.data && shell.form.getValues('tiers').length === 0) {
      shell.form.setValue('tiers', seedTiers(catalogue.data, project.tiers));
    }
  }, [catalogue.data, project.tiers, shell.form]);

  const tiers = shell.form.watch('tiers');
  const mods = {
    has_invoicing: shell.form.watch('has_invoicing'),
    has_field_force: shell.form.watch('has_field_force'),
    has_vehicles: shell.form.watch('has_vehicles'),
  };
  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('licensingDrawer.title')} description={project.display_name ?? project.name} submitLabel={t('licensingDrawer.submit')}>
      <div className="flex flex-col gap-6">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">{t('licensingDrawer.licensing')}</h3>
          <TierSeatsEditor type={project.type} catalogue={catalogue.data ?? []} tiers={tiers} onChange={(next: TierDraft[]) => shell.form.setValue('tiers', next, { shouldDirty: true })} />
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">{t('licensingDrawer.addons')}</h3>
          <AddonToggles
            value={mods}
            original={{ has_invoicing: project.has_invoicing, has_field_force: project.has_field_force, has_vehicles: project.has_vehicles }}
            onChange={(next) => {
              shell.form.setValue('has_invoicing', next.has_invoicing, { shouldDirty: true });
              shell.form.setValue('has_field_force', next.has_field_force, { shouldDirty: true });
              shell.form.setValue('has_vehicles', next.has_vehicles, { shouldDirty: true });
            }}
          />
        </div>
      </div>
    </DrawerForm>
  );
}

// ── Manage trial ──────────────────────────────────────────────────────────────
// Shown only while is_trial (covers trial + trial-expired). Extend by a preset
// (Save), or convert to a full project (a separate mutation — is_trial off, stays
// active). Both persist via the update route and both carry the project's stamp:
// the extension is the form's save; the conversion is a decision on the project as
// the drawer shows it, a refusal landing on the drawer's own line.
export function ManageTrialDrawer({ project, open, onClose }: DrawerProps) {
  // The screen's one zone (ADR-082 §2): the viewer's own — a platform / personal page.
  const display = useDisplayZone({ kind: 'person' });
  const zone = display.zone ?? 'UTC';
  const { t, i18n } = useTranslation('platform-projects');
  const toast = useToast();
  const update = useUpdatePlatformProject();
  const trialLengths = useTrialLengthSource();
  const stamped = useProjectRow(project);
  const shell = useFormShell<TrialFormValues, PlatformProjectDetail>({
    schema: trialFormSchema,
    ...stamped,
    // The extension is a choice made here, never a fact of the row: every open starts from the usual preset.
    values: () => ({ trial_length: '3m' }),
    onSubmit: async (v, { stamp }) => {
      await update.mutateAsync({ id: project.id, body: { row_version: stamp, trial_expires_at: trialExpiryISO(v.trial_length) } });
    },
    onSuccess: () => {
      toast.success(t('trialDialog.extended'));
      onClose();
    },
  });
  const currentEnd = project.trial_expires_at
    ? new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'medium', calendar: 'gregory', timeZone: zone }).format(new Date(project.trial_expires_at))
    : '';

  // Convert is a decision on the project as the drawer shows it: its stamp travels, and a refusal is the drawer's
  // own line (a stale one offers Load the latest, then the person decides again).
  const convert = async (): Promise<void> => {
    try {
      await update.mutateAsync({ id: project.id, body: { row_version: project.row_version, is_trial: false, trial_expires_at: null } });
      toast.success(t('trialDialog.converted'));
      onClose();
    } catch (err) {
      shell.refuse(err);
    }
  };

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('trialDialog.title')} description={t('trialDialog.description')} submitLabel={t('trialDialog.submit')}>
      {currentEnd && <p className="text-sm text-[var(--color-text-secondary)]">{t('trialDialog.current', { date: currentEnd })}</p>}
      <div className="sm:max-w-xs">
        <Picker
          source={trialLengths}
          label={t('trialDialog.extendBy')}
          value={shell.form.watch('trial_length')}
          onChange={(v) => shell.form.setValue('trial_length', (v as TrialFormValues['trial_length'] | null) ?? '3m', { shouldDirty: true })}
        />
      </div>
      <div className="mt-2 border-t border-[var(--color-border)] pt-4">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{t('trialDialog.convertTitle')}</h3>
        <p className="mt-1 mb-3 text-xs text-[var(--color-text-secondary)]">{t('trialDialog.convertHelp')}</p>
        <Button type="button" variant="secondary" onClick={() => void convert()}>
          {t('trialDialog.convertButton')}
        </Button>
      </div>
    </DrawerForm>
  );
}
